import React from "react";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/FinalCTA";
import { CheckCircle2, Globe2, ShieldCheck, Smartphone, CreditCard, Languages } from "lucide-react";
import { useT } from "@/hooks/useT";

export default function BookingEngine() {
  const t = useT();
  const BULLETS = [
    t("Disponueshmëria gjithmonë e përditësuar",  "Always up-to-date availability"),
    t("Çmime dhe oferta sezonale",                 "Seasonal rates and offers"),
    t("Shqip, anglisht, italisht",                  "Albanian, English, Italian"),
    t("Disa valuta me konvertim vetë",              "Multi-currency with auto conversion"),
    t("Pagesa online ose në recepsion",             "Pay online or at the front desk"),
    t("Kode promocionale dhe paketa",               "Promo codes and packages"),
    t("Me ngjyrat dhe logon e hotelit tuaj",         "Custom colours and logo"),
    t("I gatshëm për telefon dhe tablet",            "Optimised for phone and tablet"),
  ];
  const CARDS = [
    [Globe2,       t("Disa objekte",         "Multi-property"),         t("Një widget për të gjithë hotelet tuaj","One widget for all your hotels")],
    [ShieldCheck,  t("Pagesa të sigurta",     "Secure payments"),         t("Pagesa të certifikuara dhe të mbrojtura","Certified, protected transactions")],
    [Smartphone,   t("Funksionon në telefon", "Works on mobile"),         t("Më shumë rezervime nga mobile","More bookings from mobile")],
    [CreditCard,   t("Online ose në recepsion","Online or at the desk"),  t("Klienti zgjedh si dëshiron të paguajë","Guests pick how they'd like to pay")],
    [Languages,    t("3 gjuhë",               "3 languages"),             t("Shqip, anglisht, italisht","Albanian, English, Italian")],
    [CheckCircle2, t("Pa komision kanali",     "No channel commission"),   t("Çdo rezervim direkt — 100% i juaji","Every direct booking is 100% yours")],
  ];
  return (
    <>
      <SEO
        title={t("Booking Engine për Hotele — Rezervime Direkte", "Hotel Booking Engine — Direct Reservations")}
        description={t(
          "Booking engine i Akomoda për hotele dhe apartamente: rezervime direkte në website-in tuaj, pa komisione kanali, me pagesa të sigurta dhe disa gjuhë.",
          "Akomoda's booking engine for hotels and apartments: direct bookings from your own website, zero channel commissions, secure payments and multiple languages."
        )}
      />
      <PageHeader
        eyebrow="Booking Engine"
        title={t("Rezervime direkte në website-in tuaj — pa komisione", "Direct bookings on your website — zero commissions")}
        description={t(
          "Klienti rezervon në website-in tuaj. Rezervimi kalon vetë në kalendarin e recepsionit. Pa komision kanali, pa hapa në mes.",
          "The guest books right on your website. The reservation lands straight in the front-desk calendar. No channel fees, no middle steps."
        )}
        ctas={[
          { to: "/demo",            label: t("Kërko Demo","Request Demo") },
          { to: "/channel-manager", label: "Channel Manager", variant: "ghost" },
        ]}
        breadcrumb={[
          { label: t("Platforma","Platform"), to: "/platforma" },
          { label: "Booking Engine" },
        ]}
      />
      <section className="bg-surface-1">
        <div className="container-akm grid gap-10 py-14 md:grid-cols-12 md:py-20">
          <div className="md:col-span-6">
            <h2 className="h2-akm">{t("Më shumë rezervime direkte. Më pak komisione.", "More direct bookings. Fewer commissions.")}</h2>
            <p className="lead mt-3">
              {t(
                "Rezervimet direkte janë rezervimet më të vlefshme. Akomoda ju jep një widget rezervimi modern që vendoset në çdo website hoteli — me kontroll të plotë mbi përvojën dhe pa komision kanali.",
                "Direct bookings are the most valuable ones. Akomoda gives you a modern booking widget that drops into any hotel website — with full control over the experience and zero channel fees."
              )}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-ink">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#FF7A00]" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-6">
            <div className="grid grid-cols-2 gap-3">
              {CARDS.map(([Icon, tt, d]) => (
                <div key={tt} className="card-akm">
                  <Icon size={20} className="text-[#FF7A00]" />
                  <div className="mt-2 font-display text-base font-bold text-ink">{tt}</div>
                  <div className="text-sm text-slate2">{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
